import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, Quote } from "lucide-react";
import { IMAGES } from "@/data/content";
import { FadeUp, SectionHeading } from "./Reveal";

export const TESTIMONIALS = [
  {
    id: "osagie",
    headline: "Buying the property was only the beginning.",
    image: "/story-osagie-family.jpg",
    imageAlt: "Illustrative photograph of a family together at home during the festive season (Pexels)",
    quote:
      "When we decided to invest in a property in Lagos, we knew we needed more than just a house. We wanted to make the right decision for our family and eventually turn the property into a home. JÀWÀ helped us navigate the process of looking for a property by connecting us with reputable agents. Having a checklist of things to look out for before purchasing was also valuable because there is so much to consider beyond the appearance of a property. After the purchase, there was still the work of getting the house ready, from the building work to furnishing and decorating. It's one thing to buy a property, but getting it ready to live in is another project entirely. We're working towards relocating in 2027, so every step brings us closer to that goal. What started as an investment is becoming something much more personal: a home for our family and a foundation for our future in Nigeria.",
    helped:
      "Property search support, introductions to reputable agents, a pre-purchase property checklist, and support with property preparation, furnishing and decoration.",
    name: "The Osagie Family",
    journey: "Investor Journey",
    journeyHref: "/journeys/investor",
  },
  {
    id: "tunde",
    headline: "Having someone help me get organised before I landed made a difference.",
    image: "/story-tunde.jpg",
    imageAlt: "Illustrative photograph of a traveller relaxing in a premium cabin (Pexels)",
    quote:
      "Travelling to Nigeria is exciting, but there are practical things you want to have sorted before you arrive. I didn't want to land after a long flight and then have to work out everything from the airport to my accommodation. I used JÀWÀ's airport concierge service and accommodation support. Having help with the arrival process, including immigration assistance, and arrangements for getting to where I was staying made things feel much more organised. The other thing I would recommend is being prepared before your trip. JÀWÀ's A–Z Tourist Guide is the kind of resource that can help you think about the practical details of visiting Nigeria, from getting connected with a local SIM card to understanding transport and planning your stay. It made me appreciate that visiting somewhere is about more than booking a flight. Having the right arrangements and knowing where to turn for help can make the experience much easier.",
    helped:
      "Airport concierge and immigration assistance, accommodation support, transport arrangements, and practical tourist guidance through the A–Z Tourist Guide.",
    name: "Tunde",
    journey: "Tourist Journey",
    journeyHref: "/journeys/tourist",
  },
  {
    id: "musa",
    headline: "We wanted to get the important decisions right for our children.",
    image: "/story-musa-family.jpg",
    imageAlt: "Illustrative photograph of a family in traditional Nigerian dress (Pexels)",
    quote:
      "Moving to Nigeria as a family involves so many decisions. Finding somewhere to live is important, but choosing the right school and making sure the children will settle in are just as important to us. JÀWÀ's school guidance helped us focus on the questions we needed to ask and the options we needed to consider. Having a school guide to help us research our choices made the process feel more structured. We also had to think beyond the first few weeks: where we would live, how the children would get to school, and the practical support we would need to make family life work. Things like finding suitable domestic staff and getting the household organised are easy to overlook when you're concentrating on the big move. What we wanted was to make informed decisions and prepare properly, rather than arrive and discover that we'd missed something important. Having a resource focused on the realities of relocating to Nigeria helped us think about the move as a whole.",
    helped:
      "School research and guidance, the school guide, relocation planning, and practical guidance around housing, domestic staff and settling the family into everyday life.",
    name: "The Musa Family",
    journey: "Relocation Journey",
    journeyHref: "/journeys/returnee",
  },
  {
    id: "glenn",
    headline: "Getting established takes more than finding a job and somewhere to stay.",
    image: IMAGES.personaGlenn,
    imageAlt: "Glenn S. — expat journey",
    quote:
      "Coming to Nigeria for work means adjusting to a new environment while trying to get on with your job. There are lots of practical things to organise, and you don't always know where to start if you're unfamiliar with the local process. JÀWÀ's concierge support provides a point of contact for those practical arrangements. Getting connected with a Nigerian SIM card is important from day one, and for anyone staying longer term, opening a local bank account is another thing worth looking into. There are also the everyday considerations: finding suitable accommodation, arranging reliable transport and knowing where to go for the services you need. Having support with these things can take some of the pressure off while you're getting established. For me, the value of this kind of support is having somewhere to turn for practical guidance instead of having to work everything out independently in a new environment.",
    helped:
      "Concierge support, arrival planning, SIM card assistance, accommodation and transport coordination, and guidance on opening a Nigerian bank account, subject to eligibility and bank requirements.",
    name: "Glenn S.",
    journey: "Expat Journey",
    journeyHref: "/journeys/expat",
  },
];

const TestimonialCard = ({ t, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <FadeUp delay={index * 0.06}>
      <article
        className="flex h-full flex-col overflow-hidden border border-jawa-stone bg-white"
        data-testid={`testimonial-${t.id}`}
      >
        <div className="relative h-44">
          <img src={t.image} alt="" aria-hidden="true" loading="lazy" className="h-full w-full object-cover" />
          <span className="absolute left-4 top-4 bg-jawa-nearblack/70 px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-jawa-gold backdrop-blur-sm">
            {t.journey}
          </span>
        </div>
        <div className="flex flex-1 flex-col p-8 lg:p-10">
        <Quote className="h-6 w-6 text-jawa-gold" />
        <h3 className="mt-5 font-serif text-2xl font-medium leading-snug tracking-tight text-jawa-espresso">
          “{t.headline}”
        </h3>
        <AnimatePresence initial={false}>
          {open ? (
            <motion.div
              key="full"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
            >
              <p className="mt-5 text-sm leading-relaxed text-jawa-espresso/75">{t.quote}</p>
              <p className="mt-4 border-l-2 border-jawa-turquoise pl-4 text-xs leading-relaxed text-jawa-espresso/60">
                <span className="font-semibold uppercase tracking-[0.15em] text-jawa-espresso">How JÀWÀ helped: </span>
                {t.helped}
              </p>
            </motion.div>
          ) : null}
        </AnimatePresence>
        <div className="mt-auto flex items-center justify-between gap-4 pt-6">
          <div>
            <p className="font-serif text-lg text-jawa-espresso">— {t.name}</p>
            <a
              href={t.journeyHref}
              className="text-[10px] uppercase tracking-[0.25em] text-jawa-terracotta transition-colors hover:text-jawa-espresso"
            >
              {t.journey}
            </a>
          </div>
          <button
            onClick={() => setOpen(!open)}
            data-testid={`testimonial-toggle-${t.id}`}
            aria-expanded={open}
            className="flex items-center gap-1.5 border border-jawa-espresso/20 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-jawa-espresso/70 transition-colors hover:border-jawa-turquoise hover:text-jawa-espresso"
          >
            {open ? <Minus className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
            {open ? "Close" : "Read story"}
          </button>
        </div>
        </div>
      </article>
    </FadeUp>
  );
};

export const Testimonials = () => (
  <section
    id="testimonials"
    data-testid="testimonials-section"
    className="border-y border-jawa-stone bg-jawa-stone/40"
  >
    <div className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36">
      <FadeUp>
        <SectionHeading overline="Real experiences with JÀWÀ Africa" title="Told by the people who made the move." />
        <p className="mt-6 max-w-xl text-base leading-relaxed text-jawa-espresso/70">
          An investor family, a traveller, a relocating family and an expat — four journeys, in their own words.
        </p>
        <p className="mt-2 text-xs text-jawa-espresso/45">Client stories shared with permission; photographs are illustrative.</p>
      </FadeUp>
      <div className="mt-16 grid gap-6 md:grid-cols-2">
        {TESTIMONIALS.map((t, i) => (
          <TestimonialCard key={t.id} t={t} index={i} />
        ))}
      </div>
    </div>
  </section>
);
